import {
  Box,
  Center,
  Flex,
  Heading,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
} from '@chakra-ui/react'
import type { Dispatch, ReactElement, SetStateAction } from 'react'

import { pluralize } from '@/utils/helpers'

import CourseQ from '@/queries/CourseQ'
import TutorProfileQ from '@/queries/TutorProfileQ'
import UserQ from '@/queries/UserQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { SQL } from '@/client/query'
import getTutorsSearchQuery from '@/client/services/search/getTutorsSearchQuery'

import { useQueryCourses, useUpdateSelectedCourses } from '@/components/Explore/helpers'
import NotFound from '@/components/Icons/NotFound'
import { SortWidget, useSort } from '@/components/Pagination/SortWidget'
import { SearchBar, useSearch } from '@/components/Search/Search'
import { useInfiniteScroll } from '@/components/Session/helpers'
import Spinner from '@/components/Spinner'
import TutorList from '@/components/TutorList'

export default function ExploreTutors({
  disabled,
  setPageDisabled,
}: {
  disabled: boolean
  setPageDisabled: Dispatch<SetStateAction<boolean>>
}): ReactElement {
  const user = useUser()
  const coursesSelected = useQueryCourses()
  const searchParam = useSearch()

  const updateSelectedCourses = useUpdateSelectedCourses(setPageDisabled)
  const query = useQuery()
  const [queryLimit] = useInfiniteScroll()

  const courses = query.many(CourseQ.getAll({ excludeOther: true }))

  const sorts = {
    'Most Upcoming Sessions': SQL`ORDER BY (SELECT ${UserQ.countUpcomingSessionsHostedByUser()}) DESC, "id" DESC`,
    'Most Experienced': SQL`ORDER BY (SELECT ${UserQ.countPastHostedSessionsIncludingMigrated({
      requireLearnerAttendance: true,
    })}) DESC, "id" DESC`,
    'Most Followed': SQL`ORDER BY (SELECT ${UserQ.countFollowers()}) DESC, "id" DESC`,
    Newest: SQL`
      ORDER BY (
          SELECT ${TutorProfileQ.approved_at} ${TutorProfileQ.where({ userId: UserQ.id })}
          LIMIT 1
      ) DESC`,
  }
  const sort = useSort(sorts)

  const tutorsWhere = getTutorsSearchQuery({
    courseSlugs: coursesSelected,
    userId: user.id,
    searchParam,
  })

  const tutors = query.many`
        ${tutorsWhere}
        ${sort}
        LIMIT ${queryLimit}
    `
  const tutorCount =
    query<number>`
        COUNT(*)
        ${tutorsWhere}
    ` ?? 0

  const handleCloseTag = (courseSlug: string) => {
    const tempCourses = [...coursesSelected]
    const index = tempCourses.indexOf(`${courseSlug}`)
    if (index !== -1) {
      tempCourses.splice(index, 1)
    }
    updateSelectedCourses(tempCourses)
  }

  return (
    <>
      <Flex
        flexDir="column"
        position="sticky"
        top="0px"
        zIndex={2}
        boxShadow="0px 5px 3px -3px rgba(0, 0, 0, 0.15)"
        py={3}
        bgColor="bg"
      >
        <Stack
          flexDir={['column', 'row']}
          alignItems={['start', 'center']}
          mb={3}
          spacing={[4, 0]}
          justifyContent="space-between"
        >
          <Heading size="xl" as="h3">
            Tutors
          </Heading>
          <SearchBar placeholder="Search Tutors" />
        </Stack>
        <Box>
          {courses.map(entry => {
            const courseId = entry(CourseQ.id)
            const courseName = entry(CourseQ.name)
            const courseSlug = entry(CourseQ.slug)

            if (!coursesSelected.includes(`${courseSlug}`)) {
              return null
            }
            return (
              <Tag colorScheme="gray" mr={1} mb={1} key={courseId ?? 0}>
                <TagLabel>{courseName}</TagLabel>
                <TagCloseButton
                  onClick={() => handleCloseTag(`${courseSlug}`)}
                  isDisabled={disabled}
                  value={`${courseSlug}`}
                  border="none"
                  _disabled={{
                    cursor: 'not-allowed',
                  }}
                />
              </Tag>
            )
          })}
          {coursesSelected.length > 0 ? (
            <Tag
              mr={1}
              mb={1}
              color="primary.fg"
              backgroundColor="bg"
              fontWeight="bold"
              onClick={() => updateSelectedCourses([])}
              _hover={{
                color: 'primary.fg',
                cursor: 'pointer',
              }}
            >
              Reset Filters
            </Tag>
          ) : null}
        </Box>
      </Flex>
      <Box mt="24px">
        {disabled ? (
          <Center my="96px">
            <Spinner />
          </Center>
        ) : tutors.length === 0 ? (
          <Flex direction="column" align="center" textAlign="center" mt={5} padding={24}>
            <NotFound />
            <Heading size="xl" as="h3">
              Oh no!
            </Heading>
            {coursesSelected.length === 0 ? (
              <Text my="16px" maxWidth="500px">
                No tutors are available at the moment.
              </Text>
            ) : (
              <Text my="16px" maxWidth="500px">
                No tutors match this search. Please try choosing another subject.
              </Text>
            )}
          </Flex>
        ) : (
          <>
            <Stack justify="space-between" mb="24px" direction={['column', 'row']} spacing={[4, 0]}>
              <Text m={0} fontWeight="bold">
                {pluralize(tutorCount, 'Tutor')}
              </Text>
              <SortWidget sorts={sorts} />
            </Stack>
            <TutorList tutors={tutors} totalItemsCount={tutorCount} />
          </>
        )}
      </Box>
    </>
  )
}
